/**
 * 階層的なCascaderオプションを構築する汎用ビルダー
 *
 * フラットなデータから任意の階層数のCascaderオプションを構築する
 */

import * as R from 'remeda';
import type { CascaderOption } from '@/shared/ui';

/** 各階層の設定 */
type LevelConfig<T> = {
  /** CascaderOptionのvalueに設定する値 */
  getValue: (item: T) => string | null | undefined;
  /** CascaderOptionのlabelに設定する値 */
  getLabel: (item: T) => string | null | undefined;
  /** グループ化キー（省略時はgetValueを使用） */
  getGroupKey?: (item: T) => string;
};

/**
 * code:name形式のLevelConfigを生成するヘルパー
 *
 * valueにcode、labelに`${code}:${name}`形式を設定する
 */
export const codeNameLevel = <T>({
  getCode,
  getName,
}: {
  getCode: (item: T) => string | null | undefined;
  getName: (item: T) => string | null | undefined;
}): Pick<LevelConfig<T>, 'getValue' | 'getLabel'> => ({
  getValue: getCode,
  getLabel: (item) => {
    const code = getCode(item);
    const name = getName(item);
    return code && name ? `${code}:${name}` : null;
  },
});

/**
 * 階層的なCascaderオプションを構築
 *
 * @param items - フラットなデータ配列
 * @param levels - 各階層の設定（上位から順に指定）
 * @param preFilter - 初期フィルタ（省略可）
 */
export const buildHierarchicalCascaderOptions = <T>({
  items,
  levels,
  preFilter,
}: {
  items: T[] | null | undefined;
  levels: LevelConfig<T>[];
  preFilter?: (item: T) => boolean;
}): CascaderOption[] => {
  if (!items || levels.length === 0) return [];

  // 再帰的に階層を構築する関数
  const buildLevel = (data: T[], levelIndex: number): CascaderOption[] => {
    const config = levels[levelIndex];
    if (!config) return [];

    const isLastLevel = levelIndex === levels.length - 1;

    // value/labelが有効なアイテムのみを対象とする
    const validItems = R.pipe(
      data,
      R.filter((item) => Boolean(config.getValue(item) && config.getLabel(item)?.trim())),
    );

    const toOption = ({
      item,
      children,
    }: {
      item: T;
      children?: CascaderOption[];
    }): CascaderOption => {
      const value = config.getValue(item) as string;
      const label = config.getLabel(item) as string;
      return { value, label, ...(children && children.length > 0 && { children }) };
    };

    // 最下層の場合はグループ化せずにオプションを生成する
    if (isLastLevel) {
      return R.pipe(
        validItems,
        R.uniqueBy((item) => config.getValue(item)),
        R.map((item) => toOption({ item })),
      );
    }

    const getKey = config.getGroupKey ?? ((item: T) => config.getValue(item) as string);
    const cascaderGroups = R.groupBy(validItems, getKey);

    return R.pipe(
      Object.values(cascaderGroups),
      R.map((cascaderGroup) => {
        const first = cascaderGroup[0];
        const children = buildLevel(cascaderGroup, levelIndex + 1);
        return toOption({ item: first, children });
      }),
    );
  };

  const filteredItems = preFilter ? items.filter(preFilter) : items;
  return buildLevel(filteredItems, 0);
};
